import { ArrowDownOutlined, ArrowUpOutlined, DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, App, AutoComplete, Button, Drawer, Flex, Form, Input, Modal, Popconfirm, Select, Space, Table, Tag, Tooltip, Typography } from "antd";
import { useState } from "react";
import { api, unwrap, type Schemas } from "@/api/client";
import { useApplyFieldErrors, useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { useI18n } from "@/i18n";
import { planAllows } from "@/lib/plan";

type Zone = Schemas["Zone"];
type ZoneKind = Schemas["ZoneKind"];
const kinds: ZoneKind[] = ["general", "fast_moving", "bulk", "high_value", "cold"];
type ZoneValues = { code?: string; name: string; kind: ZoneKind; pickerIds?: string[] };

/** The zones of a warehouse, in walking order. */
export function useZones(warehouseId: string | undefined) {
  return useQuery({
    queryKey: ["zones", warehouseId],
    queryFn: () => unwrap(api.GET("/v1/warehouses/{warehouseID}/zones", { params: { path: { warehouseID: warehouseId! } } })),
    enabled: !!warehouseId,
  });
}

/** A zone field: pick one of the warehouse's zones or type a new code. */
export function ZoneInput({ warehouseId, value, onChange, allowEmpty }: { warehouseId: string; value?: string; onChange?: (v: string) => void; allowEmpty?: boolean }) {
  const { t } = useI18n();
  const zones = useZones(warehouseId);
  return (
    <AutoComplete
      value={value}
      onChange={(v: string) => onChange?.(v.toUpperCase())}
      options={(zones.data?.items ?? []).map((z) => ({ value: z.code, label: z.name ? `${z.code} · ${z.name}` : z.code }))}
      placeholder={allowEmpty ? t("wms.zone.none") : undefined}
      maxLength={6}
      className="z-num"
      style={{ width: "100%" }}
    />
  );
}

export function ZoneKindTag({ kind }: { kind: ZoneKind }) {
  const { t } = useI18n();
  if (kind === "general") return null;
  return <Tag color={kind === "high_value" ? "gold" : kind === "cold" ? "cyan" : kind === "fast_moving" ? "green" : undefined}>{t(`wms.zone.kind.${kind}`)}</Tag>;
}

/** Zones of a warehouse: the walking order picking routes follow, and who picks where. */
export function ZonesDrawer({ open, warehouseId, onClose }: { open: boolean; warehouseId: string; onClose: () => void }) {
  const { t, formatNumber } = useI18n();
  const { can, me } = useAuth();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const queryClient = useQueryClient();
  const manage = can("settings.warehouses", "warehouse.manage");
  const zonePicking = planAllows(me?.workspace?.plan, "zone_picking");
  const [editing, setEditing] = useState<Zone | "new" | null>(null);

  const zones = useQuery({
    queryKey: ["zones", warehouseId],
    queryFn: () => unwrap(api.GET("/v1/warehouses/{warehouseID}/zones", { params: { path: { warehouseID: warehouseId } } })),
    enabled: open,
  });
  const items = zones.data?.items ?? [];
  const reorder = useMutation({
    mutationFn: (ids: string[]) => unwrap(api.PUT("/v1/warehouses/{warehouseID}/zones/order", { params: { path: { warehouseID: warehouseId } }, body: { ids } })),
    onSuccess: (x) => {
      queryClient.setQueryData(["zones", warehouseId], x);
      void queryClient.invalidateQueries({ queryKey: ["locations"] });
    },
    onError: (err) => void message.error(errorText(err)),
  });
  const remove = useMutation({
    mutationFn: (id: string) => unwrap(api.DELETE("/v1/zones/{zoneID}", { params: { path: { zoneID: id } } })),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["zones", warehouseId] }),
    onError: (err) => void message.error(errorText(err)),
  });
  const move = (i: number, by: -1 | 1) => {
    const ids = items.map((z) => z.id);
    ids.splice(i + by, 0, ...ids.splice(i, 1));
    reorder.mutate(ids);
  };

  return (
    <Drawer open={open} onClose={onClose} title={t("wms.zone.title")} size="large" destroyOnHidden
      extra={manage && <Button type="primary" onClick={() => setEditing("new")}>{t("wms.zone.add")}</Button>}>
      <Typography.Paragraph type="secondary">{t("wms.zone.help")}</Typography.Paragraph>
      <Table<Zone>
        rowKey="id"
        size="small"
        loading={zones.isPending || reorder.isPending}
        dataSource={items}
        pagination={false}
        locale={{ emptyText: t("wms.zone.empty") }}
        columns={[
          { title: t("wms.zone.walk"), width: 70, render: (_: unknown, _z, i) => <span className="z-num">{i + 1}</span> },
          {
            title: t("wms.zone.zone"),
            render: (_: unknown, z) => (
              <Space orientation="vertical" size={0}>
                <Space size={4}>
                  <Typography.Text strong className="z-num">{z.code}</Typography.Text>
                  <ZoneKindTag kind={z.kind} />
                </Space>
                {z.name && <Typography.Text type="secondary" style={{ fontSize: 12 }}>{z.name}</Typography.Text>}
              </Space>
            ),
          },
          { title: t("wms.zone.locations"), dataIndex: "locations", width: 90, align: "right", render: (v: number) => <span className="z-num">{formatNumber(v)}</span> },
          {
            title: t("wms.zone.pickers"),
            render: (_: unknown, z) =>
              z.pickers.length === 0 ? <Typography.Text type="secondary">{t("wms.zone.anyPicker")}</Typography.Text> : z.pickers.map((p) => <Tag key={p.userId}>{p.name}</Tag>),
          },
          ...(manage
            ? [
                {
                  width: 150,
                  render: (_: unknown, z: Zone, i: number) => (
                    <Space size={2}>
                      <Button size="small" type="text" icon={<ArrowUpOutlined />} disabled={i === 0} onClick={() => move(i, -1)} aria-label={t("wms.zone.up")} />
                      <Button size="small" type="text" icon={<ArrowDownOutlined />} disabled={i === items.length - 1} onClick={() => move(i, 1)} aria-label={t("wms.zone.down")} />
                      <Button size="small" type="text" icon={<EditOutlined />} onClick={() => setEditing(z)} aria-label={t("common.edit")} />
                      <Tooltip title={z.locations > 0 ? t("wms.zone.inUse") : undefined}>
                        <Popconfirm title={t("wms.zone.deleteConfirm", { code: z.code })} onConfirm={() => remove.mutate(z.id)} disabled={z.locations > 0}>
                          <Button size="small" type="text" danger icon={<DeleteOutlined />} disabled={z.locations > 0} aria-label={t("common.delete")} />
                        </Popconfirm>
                      </Tooltip>
                    </Space>
                  ),
                },
              ]
            : []),
        ]}
      />
      {!zonePicking && <Alert type="info" showIcon style={{ marginTop: 16 }} title={t("wms.zone.planNote")} />}
      <ZoneModal zone={editing} warehouseId={warehouseId} zonePicking={zonePicking} onClose={() => setEditing(null)} />
    </Drawer>
  );
}

function ZoneModal({ zone, warehouseId, zonePicking, onClose }: { zone: Zone | "new" | null; warehouseId: string; zonePicking: boolean; onClose: () => void }) {
  const { t } = useI18n();
  const { can } = useAuth();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const applyFieldErrors = useApplyFieldErrors();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<ZoneValues>();
  const creating = zone === "new";
  const assign = zonePicking && can("warehouse.manage");
  const workers = useQuery({
    queryKey: ["workers", warehouseId],
    queryFn: () => unwrap(api.GET("/v1/warehouses/{warehouseID}/workers", { params: { path: { warehouseID: warehouseId } } })),
    enabled: zone !== null && assign,
  });
  const save = useMutation({
    mutationFn: (v: ZoneValues) => {
      const body = { name: v.name ?? "", kind: v.kind, ...(assign ? { pickerIds: v.pickerIds ?? [] } : {}) };
      return creating
        ? unwrap(api.POST("/v1/warehouses/{warehouseID}/zones", { params: { path: { warehouseID: warehouseId } }, body: { ...body, code: v.code ?? "" } }))
        : unwrap(api.PATCH("/v1/zones/{zoneID}", { params: { path: { zoneID: (zone as Zone).id } }, body }));
    },
    onSuccess: () => {
      void message.success(t("wms.zone.saved"));
      void queryClient.invalidateQueries({ queryKey: ["zones", warehouseId] });
      onClose();
    },
    onError: (err) => {
      if (!applyFieldErrors(form, err)) void message.error(errorText(err));
    },
  });
  const initial: ZoneValues =
    zone && zone !== "new"
      ? { name: zone.name, kind: zone.kind, pickerIds: zone.pickers.map((p) => p.userId) }
      : { code: "", name: "", kind: "general", pickerIds: [] };
  return (
    <Modal
      open={zone !== null}
      title={creating ? t("wms.zone.add") : t("wms.zone.edit", { code: (zone as Zone | null)?.code ?? "" })}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={save.isPending}
      onOk={() => form.submit()}
      onCancel={onClose}
      destroyOnHidden
    >
      <Form form={form} layout="vertical" requiredMark={false} initialValues={initial} onFinish={(v) => save.mutate(v)}>
        <Flex gap={12}>
          {creating && (
            <Form.Item name="code" label={t("wms.zone.code")} rules={[{ required: true, pattern: /^[A-Za-z0-9]{1,6}$/, message: t("field.invalid") }]} style={{ width: 120 }}>
              <Input maxLength={6} className="z-num" />
            </Form.Item>
          )}
          <Form.Item name="name" label={t("wms.zone.name")} style={{ flex: 1 }}>
            <Input maxLength={80} placeholder={t("wms.zone.namePlaceholder")} />
          </Form.Item>
        </Flex>
        <Form.Item name="kind" label={t("wms.zone.kind")}>
          <Select options={kinds.map((k) => ({ value: k, label: t(`wms.zone.kind.${k}`) }))} />
        </Form.Item>
        {assign && (
          <Form.Item name="pickerIds" label={t("wms.zone.pickers")} extra={t("wms.zone.pickersHelp")}>
            <Select
              mode="multiple"
              allowClear
              loading={workers.isPending}
              options={(workers.data?.items ?? []).map((w) => ({ value: w.userId, label: w.name }))}
              showSearch={{ filterOption: (input, o) => String(o?.label ?? "").toLowerCase().includes(input.toLowerCase()) }}
            />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
}
